import styled from "styled-components";

/*
 * Shared layout primitives for the Safemate device settings tabs.
 * Establishes a consistent, responsive structure across every form-style
 * tab (regardless of device): a padded column of divider-separated
 * sections, each with an optional title/description and a responsive grid
 * of fields or checkboxes. Originally introduced for the GX-8 tabs and
 * promoted here so all settings tabs share one visual language.
 */

export const SettingsLayout = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  padding: 0 15px;
`;

export const Section = styled.section`
  padding: 24px 0;
  border-bottom: 1px solid ${props => props.theme.colors.borderColor};

  &:first-child {
    padding-top: 8px;
  }

  &:last-child {
    border-bottom: none;
    padding-bottom: 8px;
  }
`;

export const SectionTitle = styled.h3`
  margin: 0 0 4px;
  font-size: ${props => props.theme.font.normal};
  font-weight: 600;
  color: ${props => props.theme.colors.textPrimary};
`;

export const SectionDesc = styled.p`
  margin: 0 0 16px;
  max-width: 640px;
  font-size: ${props => props.theme.font.small};
  color: ${props => props.theme.colors.textSecondary};
  line-height: 1.5;
`;

export const FieldGrid = styled.div<{ dimmed?: boolean }>`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 20px 40px;
  opacity: ${props => (props.dimmed ? 0.55 : 1)};
  transition: opacity 0.15s ease;
`;

export const CheckGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 14px 32px;
`;

export const ToggleRow = styled.div`
  display: flex;
  align-items: center;
`;

export const Hint = styled.p`
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0 0 16px;
  font-size: ${props => props.theme.font.small};
  font-style: italic;
  color: ${props => props.theme.colors.textSecondary};
`;

/* ── Schedule (inactivity) ───────────────────────────────────────────── */

export const ScheduleGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 20px;
`;

export const ScheduleCard = styled.div`
  border: 1px solid ${props => props.theme.colors.borderColor};
  border-radius: ${props => props.theme.colors.borderRadius};
  background-color: ${props => props.theme.colors.backgroundSecondary};
  padding: 16px 18px 18px;
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

export const ScheduleCardHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: ${props => props.theme.font.normal};
  font-weight: 600;
  color: ${props => props.theme.colors.textPrimary};
`;

export const FieldLabel = styled.label`
  display: block;
  margin: 0 0 6px;
  font-size: ${props => props.theme.font.small};
  color: ${props => props.theme.colors.textSecondary};
`;

export const TimeRange = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 10px;
  flex-wrap: wrap;
`;

export const TimeField = styled.div`
  display: flex;
  flex-direction: column;

  input {
    width: 118px;
  }
`;

export const TimeSeparator = styled.span`
  padding-bottom: 10px;
  color: ${props => props.theme.colors.textSecondary};
`;

export const TooltipAlign = styled.div`
  display: flex;
  align-items: center;
  padding-bottom: 8px;
`;
